import React, { useState } from 'react';
import { Contact, InboxThread } from '../types';
import { Button, Input, Drawer, Card, Badge, notify } from '../components/UI';
import { Search, Plus, Mail, Phone, MapPin, Trash2, Edit3, User, Copy, Briefcase, MessageSquare } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@/core/db-adapter/useQuery';
import { contactsApi, inboxApi } from '@/core/adapter-api';

interface ContactsPageProps {
    type: 'client' | 'supplier';
}

export const ContactsPage = ({ type }: ContactsPageProps) => {
    const contacts = useQuery(() => contactsApi.byType(type), [type], ['contacts']) ?? [];
    const [search, setSearch] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingContact, setEditingContact] = useState<Contact | null>(null);
    const navigate = useNavigate();

    const handleSendMessage = async (contact: Contact) => {
        try {
            let thread = await inboxApi.threads.getByParty(contact.id);
            if (!thread) {
                const newThread: InboxThread = {
                    id: uuidv4(),
                    partyId: contact.id,
                    partyType: contact.type,
                    title: contact.name,
                    createdAt: Date.now(),
                    updatedAt: Date.now(),
                };
                await inboxApi.threads.upsert(newThread);
                thread = newThread;
            }
            navigate(`/communications?threadId=${thread.id}`);
        } catch {
            notify('Error al iniciar conversación', 'error');
        }
    };

    const isClient = type === 'client';
    const labelSingular = isClient ? 'Cliente' : 'Proveedor';
    const labelPlural = isClient ? 'Clientes' : 'Proveedores';

    const filtered = contacts.filter(c =>
        c.name.toLowerCase().includes(search.toLowerCase()) ||
        (c.email || '').toLowerCase().includes(search.toLowerCase())
    );

    const handleEdit = (contact: Contact) => { setEditingContact(contact); setIsModalOpen(true); };
    const handleNew = () => { setEditingContact(null); setIsModalOpen(true); };

    const handleDuplicate = async (contact: Contact) => {
        await contactsApi.add({ ...contact, id: uuidv4(), name: `${contact.name} (Copia)`, createdAt: Date.now(), updatedAt: Date.now() });
        notify(`${labelSingular} duplicado con éxito`, 'success');
    };

    const handleDelete = async (id: string) => {
        if (confirm(`¿Eliminar este ${labelSingular.toLowerCase()}?`)) {
            await contactsApi.delete(id);
            notify(`${labelSingular} eliminado`, 'success');
        }
    };

    const handleSave = async (e: React.FormEvent) => {
        e.preventDefault();
        const form = e.target as HTMLFormElement;
        const formData = new FormData(form);
        const data: Partial<Contact> = {
            name: formData.get('name') as string,
            nif: formData.get('nif') as string,
            email: formData.get('email') as string,
            phone: formData.get('phone') as string,
            address: formData.get('address') as string,
            city: formData.get('city') as string,
            zip: formData.get('zip') as string,
            province: formData.get('province') as string,
            country: formData.get('country') as string,
            type,
            notes: formData.get('notes') as string,
            updatedAt: Date.now()
        };
        try {
            if (editingContact) {
                await contactsApi.update(editingContact.id, data);
                notify(`${labelSingular} actualizado`, 'success');
            } else {
                await contactsApi.add({ id: uuidv4(), createdAt: Date.now(), ...data } as Contact);
                notify(`${labelSingular} creado`, 'success');
            }
            setIsModalOpen(false);
        } catch {
            notify('Error al guardar', 'error');
        }
    };

    return (
        <div className="p-8 max-w-7xl mx-auto space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-2xl font-bold text-gray-800">{labelPlural}</h1>
                    <p className="text-gray-500">Gestiona tus {labelPlural.toLowerCase()}</p>
                </div>
                <Button onClick={handleNew}><Plus size={18} /> Nuevo {labelSingular}</Button>
            </div>

            <div className="flex space-x-4">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                    <input
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none bg-white text-gray-900"
                        placeholder="Buscar por nombre, email..."
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                    />
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 relative z-10">
                {filtered.map(contact => (
                    <Card key={contact.id} className="p-6 hover:shadow-[0_8px_30px_rgb(0,0,0,0.06)] group relative overflow-hidden bg-white">
                        <div className={`absolute top-0 right-0 w-32 h-32 rounded-bl-full opacity-[0.03] transition-transform duration-500 group-hover:scale-125 ${isClient ? 'bg-blue-600' : 'bg-orange-600'}`}></div>
                        
                        <div className="absolute top-4 right-4 flex space-x-1 opacity-0 group-hover:opacity-100 transition-all duration-300 translate-y-1 group-hover:translate-y-0 bg-white/90 backdrop-blur-sm p-1 rounded-xl ring-1 ring-gray-900/5 shadow-sm z-20">
                            <button onClick={() => handleSendMessage(contact)} className="text-green-600 hover:bg-green-50 p-1.5 rounded-lg transition-colors" title="Enviar Mensaje"><MessageSquare size={16} /></button>
                            <button onClick={() => handleDuplicate(contact)} className="text-gray-500 hover:text-blue-600 hover:bg-gray-50 p-1.5 rounded-lg transition-colors" title="Duplicar"><Copy size={16} /></button>
                            <button onClick={() => handleEdit(contact)} className="text-blue-600 hover:bg-blue-50 p-1.5 rounded-lg transition-colors" title="Editar"><Edit3 size={16} /></button>
                            <button onClick={() => handleDelete(contact.id)} className="text-red-500 hover:bg-red-50 p-1.5 rounded-lg transition-colors" title="Eliminar"><Trash2 size={16} /></button>
                        </div>

                        <div className="flex items-center mb-5 relative z-10">
                            <div className={`w-12 h-12 rounded-2xl flex items-center justify-center text-white font-bold mr-4 shadow-inner ring-1 ring-white/20 bg-gradient-to-br ${isClient ? 'from-blue-400 to-blue-600 shadow-blue-500/20' : 'from-orange-400 to-orange-600 shadow-orange-500/20'}`}>
                                <span className="text-lg drop-shadow-sm">{contact.name.charAt(0).toUpperCase()}</span>
                            </div>
                            <div className="flex-1 min-w-0 pr-8">
                                <h3 className="font-bold text-gray-900 truncate text-base leading-tight">{contact.name}</h3>
                                <div className="mt-1"><Badge color={isClient ? 'blue' : 'orange'}>{labelSingular}</Badge></div>
                            </div>
                        </div>

                        <div className="space-y-3 text-[13px] text-gray-500 font-medium relative z-10 bg-gray-50/50 p-4 rounded-xl ring-1 ring-inset ring-gray-100/50">
                            {contact.email && <div className="flex items-center"><Mail size={14} className="mr-2.5 text-gray-400 flex-shrink-0" /> <span className="truncate">{contact.email}</span></div>}
                            {contact.phone && <div className="flex items-center"><Phone size={14} className="mr-2.5 text-gray-400 flex-shrink-0" /> <span>{contact.phone}</span></div>}
                            {contact.address && <div className="flex items-center"><MapPin size={14} className="mr-2.5 text-gray-400 flex-shrink-0" /> <span className="truncate">{contact.address}{contact.city ? `, ${contact.city}` : ''}</span></div>}
                            <div className="pt-1"><span className="font-mono text-xs bg-white ring-1 ring-gray-200 px-2 py-0.5 rounded-md text-gray-500 shadow-sm inline-block">{contact.nif || 'Sin NIF'}</span></div>
                        </div>
                    </Card>
                ))}
            </div>

            {filtered.length === 0 && (
                <div className="text-center py-12 text-gray-400">
                    {isClient ? <User size={48} className="mx-auto mb-4 opacity-20" /> : <Briefcase size={48} className="mx-auto mb-4 opacity-20" />}
                    <p>No se encontraron {labelPlural.toLowerCase()}.</p>
                </div>
            )}

            <Drawer isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={editingContact ? `Editar ${labelSingular}` : `Nuevo ${labelSingular}`}>
                <form key={editingContact ? editingContact.id : 'new'} onSubmit={handleSave} className="space-y-6">
                    <input type="hidden" name="type" value={type} />
                    <Input name="name" label="Nombre / Razón Social" defaultValue={editingContact?.name} required />
                    <Input name="nif" label="NIF / CIF" defaultValue={editingContact?.nif} />
                    <div className="grid grid-cols-2 gap-4">
                        <Input name="email" label="Email" type="email" defaultValue={editingContact?.email} />
                        <Input name="phone" label="Teléfono" defaultValue={editingContact?.phone} />
                    </div>
                    <Input name="address" label="Dirección" defaultValue={editingContact?.address} />
                    <div className="grid grid-cols-3 gap-4">
                        <Input name="zip" label="Código Postal" defaultValue={editingContact?.zip} />
                        <div className="col-span-2"><Input name="city" label="Ciudad" defaultValue={editingContact?.city} /></div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <Input name="province" label="Provincia" defaultValue={editingContact?.province} />
                        <Input name="country" label="País" defaultValue={editingContact?.country || 'España'} />
                    </div>
                    <div>
                        <label className="text-[11px] font-bold text-gray-500 uppercase tracking-wider block mb-1.5">Notas Internas</label>
                        <textarea name="notes" className="w-full px-3.5 py-2.5 border-0 bg-gray-50 text-gray-900 rounded-xl mt-1 h-24 outline-none transition-all duration-200 text-sm ring-1 ring-inset ring-gray-200 focus:ring-2 focus:ring-[var(--accent-blue)] focus:bg-white resize-none shadow-sm" defaultValue={editingContact?.notes}></textarea>
                    </div>
                    <div className="pt-6 flex justify-end space-x-3 border-t border-gray-100">
                        <Button variant="ghost" onClick={() => setIsModalOpen(false)}>Cancelar</Button>
                        <Button type="submit">{editingContact ? 'Guardar Cambios' : `Crear ${labelSingular}`}</Button>
                    </div>
                </form>
            </Drawer>
        </div>
    );
};
